import React from 'react';
import { 
  Grid, 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Card, 
  CardContent, 
  CardHeader,
  Chip
} from '@mui/material';
import DetailTemplate from '../../common/template/DetailTemplate';

const ProjectDetailInfo = ({ project, estimate }) => {
  // 정보 표시를 위한 헬퍼 함수
  const InfoItem = ({ label, value, xs = 6, md = 4, lg = 3 }) => (
    <Grid item xs={xs} md={md} lg={lg}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" component="div" sx={{ mb: 0.5, color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography variant="body1" component="div" style={{ whiteSpace: 'pre-line' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Grid>
  );

  // 섹션 헤더 컴포넌트
  const SectionHeader = ({ title }) => (
    <Typography 
      variant="h6" 
      sx={{ 
        mb: 2, 
        mt: 1, 
        fontWeight: 'medium',
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        paddingBottom: 0.5,
        display: 'inline-block'
      }}
    >
      {title}
    </Typography>
  );

  // 금액 형식화 함수
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `₩ ${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  return (
    <Box>
      <DetailTemplate title="기회 확인">
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <SectionHeader title="프로젝트 기본 정보" />
          <Grid container spacing={3}>
            <InfoItem 
              label="프로젝트 타입" 
              value={project?.prjTyp === 'N' ? '일반' : project?.prjTyp === 'M' ? '유지보수' : project?.prjTyp} 
            />
            <InfoItem 
              label="사업명" 
              value={project?.cstNm} 
            />
            <InfoItem 
              label="고객명" 
              value={project?.partnerInc} 
            />
            <InfoItem 
              label="기회구분" 
              value={project?.chanceTyp} 
            />
            <InfoItem 
              label="프로젝트 중요도" 
              value={project?.prjImpt} 
            />
            <InfoItem 
              label="거래처 유형" 
              value={project?.partnerTyp} 
            />
            <InfoItem 
              label="예산여부" 
              value={project?.bugetYn} 
            />
          </Grid>

          <Divider sx={{ my: 3 }} />

          <SectionHeader title="담당자 정보" />
          <Grid container spacing={3}>
            <InfoItem 
              label="담당자" 
              value={project?.chargeNm} 
            />
            <InfoItem 
              label="담당자 이메일" 
              value={project?.chargeEml} 
            />
            <InfoItem 
              label="담당자 연락처" 
              value={project?.chargeTel} 
            />
          </Grid>

          {project?.forcastTxt && (
            <>
              <Divider sx={{ my: 3 }} />
              <SectionHeader title="참고사항" />
              <Typography 
                variant="body1" 
                component="div" 
                style={{ whiteSpace: 'pre-line' }}
                sx={{ mb: 2, px: 1 }}
              >
                {project?.forcastTxt}
              </Typography>
            </>
          )}
        </Paper>

        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <SectionHeader title="기회분석" />
          <Grid container spacing={3}>
            <InfoItem 
              label="산업체 분류" 
              value={project?.incTyp} 
            />
            <InfoItem 
              label="아이템 구분" 
              value={project?.solutionTyp} 
            />
            <InfoItem 
              label="제품 여부" 
              value={project?.productYn} 
            />
            <InfoItem 
              label="매출처" 
              value={project?.vendorCmpNm} 
            />
            <InfoItem 
              label="매출처 담당자" 
              value={project?.vendorEmpNm} 
            />
            <InfoItem 
              label="매출(수금)예정일"
              value={project?.expDay} 
            />
          </Grid>

          <Divider sx={{ my: 3 }} />

          <SectionHeader title="프로젝트 담당자" />
          <Grid container spacing={3}>
            <InfoItem 
              label="PM" 
              value={project?.pmNm} 
            />
            <InfoItem 
              label="PL" 
              value={project?.plNm} 
            />
            <InfoItem 
              label="기술 담당자" 
              value={project?.techNm} 
            />
            <InfoItem 
              label="영업 담당자" 
              value={project?.crtNm} 
            />
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <SectionHeader title="원가 산출 내역" />
          {project?.expDay && project?.expSales && project.expBenefitRate ? (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 3,
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: 1,
              boxShadow: 'inset 0 0 5px rgba(0,0,0,0.05)'
            }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">매입금액</Typography>
                <Typography variant="h6">{formatCurrency(project?.expBuy)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">예상 수주금액</Typography>
                <Typography variant="h6">{formatCurrency(project?.expSales)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">예상 이익률</Typography>
                <Typography variant="h6">{project?.expBenefitRate}%</Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              작성된 원가 산출 내역이 없습니다.
            </Typography>
          )}
        </Paper>

        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <SectionHeader title="견적" />
          {estimate ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={3}>
                  <InfoItem 
                    label="견적 번호" 
                    value={estimate?.qteNo} 
                  />
                  <InfoItem 
                    label="견적일자" 
                    value={estimate?.qteRegDt ? estimate?.qteRegDt : estimate?.crtDt} 
                  />
                  <InfoItem 
                    label="견적 유효일자" 
                    value={estimate?.qteValidDt} 
                  />
                  <InfoItem 
                    label="총 견적 금액" 
                    value={formatCurrency(estimate?.qtePrice)} 
                  />
                  <InfoItem 
                    label="유지보수기간" 
                    value={estimate?.mntTor} 
                  />
                </Grid>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <SectionHeader title="견적 상세 정보" />
              <Grid container spacing={3}>
                <InfoItem 
                  label="납품 장소" 
                  value={estimate?.destPlace} 
                  xs={12}
                  md={6}
                />
                <InfoItem 
                  label="발행 조건" 
                  value={estimate?.qteCondt} 
                  xs={12}
                  md={6}
                />
                <InfoItem 
                  label="결제 조건" 
                  value={estimate?.payCond} 
                  xs={12}
                />
              </Grid>
              
              {(estimate?.qteDesc1 || estimate?.qteDesc2 || estimate?.qteDesc3) && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <SectionHeader title="견적서 설명" />
                  <Grid container spacing={3}>
                    {estimate?.qteDesc1 && (
                      <InfoItem 
                        label="견적서 설명 1" 
                        value={estimate?.qteDesc1} 
                        xs={12}
                      />
                    )}
                    {estimate?.qteDesc2 && (
                      <InfoItem 
                        label="견적서 설명 2" 
                        value={estimate?.qteDesc2} 
                        xs={12}
                      />
                    )}
                    {estimate?.qteDesc3 && (
                      <InfoItem 
                        label="견적서 설명 3" 
                        value={estimate?.qteDesc3} 
                        xs={12}
                      />
                    )}
                  </Grid>
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              현재 활성화된 견적서가 없습니다.
            </Typography>
          )}
        </Paper>
      </DetailTemplate>
    </Box>
  );
};

export default ProjectDetailInfo;
